// src/utils/useTheme.js
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// PINK HEAVY NAVIROSE Theme
export const defaultTheme = {
  bodyBg: '#FFFFFF',
  cardBg: '#FFFFFF',
  headerBg: '#FFFFFF',
  footerBg: '#071B36',
  topBarBg: '#071B36',
  primaryText: '#9B3F5B',
  secondaryText: '#DC829A',
  headerText: '#9B3F5B',
  footerText: '#F5AEC0',
  topBarText: '#F5AEC0',
  buttonBg: '#F5AEC0',
  buttonText: '#FFFFFF',
  buttonHoverBg: '#DC829A',
  accentColor: '#DC829A',
  linkColor: '#DC829A',
  badgeColor: '#DC829A',
  borderColor: '#F5AEC0',
  bottomNavBg: '#FFFFFF',
  bottomNavActiveBg: '#F5AEC0',
  bottomNavActiveText: '#FFFFFF',
  bottomNavInactiveText: '#DC829A',
};

export const useTheme = () => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  const fetchTheme = async () => {
    try {
      const docRef = doc(db, 'settings', 'theme');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const fullTheme = { ...defaultTheme, ...data };

        // ✅ Force apply CSS variables immediately
        applyThemeToDOM(fullTheme);

        setTheme(fullTheme);
        localStorage.setItem('siteTheme', JSON.stringify(fullTheme));
      } else {
        applyThemeToDOM(defaultTheme);
        setTheme(defaultTheme);
        localStorage.setItem('siteTheme', JSON.stringify(defaultTheme));
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
      applyThemeToDOM(defaultTheme);
      setTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTheme();

    const handleUpdate = () => fetchTheme();
    window.addEventListener('themeUpdated', handleUpdate);

    const handleStorage = (e) => {
      if (e.key === 'siteTheme') fetchTheme();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('themeUpdated', handleUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return { theme, loading };
};

// ✅ Helper function to apply theme to CSS variables immediately
function applyThemeToDOM(theme) {
  const root = document.documentElement;

  // Map theme keys to CSS variable names
  const cssVarMap = {
    bodyBg: '--color-body-bg',
    cardBg: '--color-card-bg',
    headerBg: '--color-header-bg',
    footerBg: '--color-footer-bg',
    topBarBg: '--color-topbar-bg',
    primaryText: '--color-primary-text',
    secondaryText: '--color-secondary-text',
    headerText: '--color-header-text',
    footerText: '--color-footer-text',
    topBarText: '--color-topbar-text',
    buttonBg: '--color-button-bg',
    buttonText: '--color-button-text',
    buttonHoverBg: '--color-button-hover-bg',
    accentColor: '--color-accent',
    linkColor: '--color-link',
    badgeColor: '--color-badge',
    borderColor: '--color-border',
    bottomNavBg: '--color-bottomnav-bg',
    bottomNavActiveBg: '--color-bottomnav-active-bg',
    bottomNavActiveText: '--color-bottomnav-active-text',
    bottomNavInactiveText: '--color-bottomnav-inactive-text',
  };

  Object.entries(cssVarMap).forEach(([key, cssVar]) => {
    if (theme[key]) {
      root.style.setProperty(cssVar, theme[key]);
    }
  });

  // Also update body background
  if (theme.bodyBg) {
    document.body.style.backgroundColor = theme.bodyBg;
  }
  if (theme.primaryText) {
    document.body.style.color = theme.primaryText;
  }
}

export default useTheme;